import { GiftBoxResponse, GiftCouponStatus } from '@models/gift';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { getReceivedCouponStatus, getReceivedSenderName } from '../../../utils';

interface InfoRow {
    label: string;
    value: string;
}

function formatDateTime(value?: Date | string) {
    if (!value) return '';
    return dayjs(value).format('YYYY.MM.DD HH:mm');
}

const STATUS_LABEL_KEYS: Partial<Record<GiftCouponStatus, string>> = {
    [GiftCouponStatus.ISSUED]: 'giftReceived.status.available',
    [GiftCouponStatus.USED]: 'giftReceived.status.used',
    [GiftCouponStatus.EXPIRED]: 'giftReceived.status.expired',
    [GiftCouponStatus.CANCELED]: 'giftReceived.status.canceled',
};

/**
 * 상태별 info 행. 사용가능매장은 모든 경우에 붙인다.
 * - 사용 가능: 보낸 사람 / 선물 상태 / 유효기간 / 사용가능매장
 * - 사용 완료: 보낸 사람 / 선물 상태 / 사용 일시 / 사용가능매장
 * - 기간 만료: 보낸 사람 / 선물 상태 / 유효기간 / 사용가능매장
 * - 결제취소: 보낸 사람 / 선물 상태 / 취소 일시 / 사용가능매장
 * - 금액권: 보낸 사람 / 선물 상태 / (결제취소 시 취소 일시) / 유효기간 / 사용가능매장 / 잔액
 */
function useGiftInfoRows(giftBox: GiftBoxResponse): InfoRow[] {
    const { t } = useTranslation('gift');
    const coupon = giftBox.giftCoupon;
    const status = getReceivedCouponStatus(giftBox);
    const statusLabelKey = status && STATUS_LABEL_KEYS[status];

    const senderRow = { label: t('giftReceived.sender'), value: getReceivedSenderName(giftBox, t('giftReceived.self')) };
    const validPeriodRow = {
        label: t('giftReceived.validPeriod'),
        value: coupon?.expiredAt
            ? t('giftReceived.validUntil', { date: formatDateTime(coupon.expiredAt) })
            : '-',
    };
    const statusRow = { label: t('giftReceived.giftStatus'), value: statusLabelKey ? t(statusLabelKey) : '-' };
    const canceledAtRow = { label: t('giftReceived.canceledAt'), value: coupon?.canceledAt ? formatDateTime(coupon.canceledAt) : '-' };
    const availableShopsRow = { label: t('giftReceived.availableShops'), value: t('giftReceived.allShops') };

    if (giftBox.couponType === 'BALANCE') {
        // TODO: 잔액이 남아 있어도 coupon.status 가 USED 로 내려오는 경우가 있어
        //  잔액/유효기간을 함께 본 상태 조건 정리 필요. 우선 status 그대로 노출한다.
        return [
            senderRow,
            statusRow,
            ...(status === GiftCouponStatus.CANCELED ? [canceledAtRow] : []),
            validPeriodRow,
            availableShopsRow,
            {
                label: t('giftReceived.balance'),
                value: giftBox.balance != null ? `${giftBox.balance.toLocaleString()}${t('item.priceUnit')}` : '-',
            },
        ];
    }

    const rows: InfoRow[] = [senderRow, statusRow];

    switch (status) {
    case GiftCouponStatus.ISSUED:
        return [...rows, validPeriodRow, availableShopsRow];
    case GiftCouponStatus.USED:
        return [...rows, { label: t('giftReceived.usedAt'), value: coupon?.usedAt ? formatDateTime(coupon.usedAt) : '-' }, availableShopsRow];
    case GiftCouponStatus.EXPIRED:
        return [...rows, validPeriodRow, availableShopsRow];
    case GiftCouponStatus.CANCELED:
        return [...rows, canceledAtRow, availableShopsRow];
    default:
        return [...rows, availableShopsRow];
    }
}

export default useGiftInfoRows;
